import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addMonths } from "date-fns";
import enUS from "date-fns/locale/en-US";
import es from "date-fns/locale/es";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { http } from "../../lib/http";
import PageChrome from "../../components/shell/PageChrome";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Printer, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { useSearchParams, useOutletContext } from "react-router-dom";

const locales = { "en-US": enUS, es };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });
const DnDCalendar = withDragAndDrop(Calendar);

function colorFor(it, rooms) {
  if (it.kind === "holiday") return "#94a3b8";
  if (it.kind === "event") return it.color || "#C8102E";
  const r = rooms.find(rr => rr.id === it.room_id);
  return r?.color || "#0A1F44";
}

function EventEditModal({ open, onClose, initial, rooms, teachers, onSaved, isAdmin }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({});
  const [conflicts, setConflicts] = useState([]);
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (initial) setForm({ ...initial });
    else setForm({ title: "", type: "other", start: "", end: "", teacher_user_id: "", room_id: "", notes: "" });
    setConflicts([]); setOverride(false); setReason("");
  }, [initial, open]);

  const submit = async () => {
    try {
      const params = override && isAdmin ? "?override=true" : "";
      const body = { ...form, override: override && isAdmin, override_reason: reason || undefined };
      if (initial?.id) await http.patch(`/calendar/events/${initial.id}${params}`, body);
      else await http.post(`/calendar/events${params}`, body);
      toast.success(t("calendar.eventSaved"));
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.conflicts) { setConflicts(d.conflicts); toast.error(t("classes.conflictsFound")); }
      else toast.error("Save failed");
    }
  };

  const remove = async () => {
    if (!initial?.id || !window.confirm("Delete event?")) return;
    try { await http.delete(`/calendar/events/${initial.id}`); toast.success("Deleted"); onSaved(); }
    catch { toast.error("Delete failed"); }
  };

  const blocking = conflicts.filter(c => c.type !== "on_holiday");
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
          {initial?.id ? t("calendar.editEvent") : t("calendar.newEvent")}
        </DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>{t("calendar.title")} *</Label>
            <Input value={form.title || ""} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="event-form-title"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("calendar.type")}</Label>
              <Select value={form.type || "other"} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  {["trial","meeting","exam","academy_event","absence_note","other"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("classes.teacher")}</Label>
              <Select value={form.teacher_user_id || ""} onValueChange={v => setForm({ ...form, teacher_user_id: v })}>
                <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">—</SelectItem>
                  {teachers.map(t => <SelectItem key={t.user_id} value={t.user_id}>{t.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("calendar.start")}</Label>
              <Input type="datetime-local" value={(form.start || "").slice(0,16)} onChange={e => setForm({ ...form, start: e.target.value })}/></div>
            <div><Label>{t("calendar.end")}</Label>
              <Input type="datetime-local" value={(form.end || "").slice(0,16)} onChange={e => setForm({ ...form, end: e.target.value })}/></div>
          </div>
          <div><Label>{t("classes.room")}</Label>
            <Select value={form.room_id || ""} onValueChange={v => setForm({ ...form, room_id: v })}>
              <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="">—</SelectItem>
                {rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>{t("classes.notes")}</Label>
            <Textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })}/></div>
          {blocking.length > 0 && (
            <div className="space-y-2" data-testid="event-conflicts">
              {blocking.map((c, i) => (
                <div key={i} className="flex gap-2 p-2 rounded border border-[#C8102E]/40 bg-[#C8102E]/5 text-sm">
                  <AlertTriangle className="h-4 w-4 text-[#C8102E]"/>
                  <span>{c.message}</span>
                </div>
              ))}
              {isAdmin && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={override} onChange={e => setOverride(e.target.checked)}/>
                  {t("classes.override")}
                </label>
              )}
              {override && <Textarea placeholder={t("classes.overridePlaceholder")} value={reason} onChange={e => setReason(e.target.value)}/>}
            </div>
          )}
        </div>
        <DialogFooter>
          {initial?.id && <Button variant="destructive" onClick={remove}>{t("common.delete")}</Button>}
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} className="bg-[#0A1F44]" disabled={blocking.length > 0 && !(isAdmin && override && reason.trim())}
                  data-testid="event-form-save">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CalendarPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { permissions } = useOutletContext() || {};
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [view, setView] = useState("week");
  const [date, setDate] = useState(new Date());
  const [editingEvent, setEditingEvent] = useState(null);
  const [creating, setCreating] = useState(false);
  const [teacherFilter, setTeacherFilter] = useState("all");
  const [roomFilter, setRoomFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const canCreate = !!permissions?.calendar?.create;
  const canEdit = !!permissions?.calendar?.edit;
  const isAdmin = user?.role === "admin";

  const fetchItems = useCallback(async () => {
    const from = startOfMonth(addMonths(date, -1)).toISOString();
    const to = endOfMonth(addMonths(date, 1)).toISOString();
    const qp = new URLSearchParams({ from, to });
    if (teacherFilter !== "all") qp.append("teacher_id", teacherFilter);
    if (roomFilter !== "all") qp.append("room_id", roomFilter);
    if (typeFilter !== "all") qp.append("type", typeFilter);
    const [c, r, u] = await Promise.all([
      http.get(`/calendar?${qp.toString()}`),
      http.get(`/rooms`),
      http.get(`/classes/teachers-in-scope`).catch(() => ({ data: { items: [] }})),
    ]);
    setItems(c.data.items || []);
    setRooms(r.data.items || []);
    setTeachers(u.data.items || []);
  }, [date, teacherFilter, roomFilter, typeFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const events = useMemo(() => items.map(it => ({
    id: it.id,
    title: it.title,
    start: new Date(it.start),
    end: new Date(it.end),
    allDay: !!it.all_day,
    resource: it,
  })), [items]);

  const eventStyle = (event) => {
    const it = event.resource;
    const bg = colorFor(it, rooms);
    const style = { backgroundColor: bg, borderColor: bg, color: "#fff", borderRadius: 6,
                    border: it.kind === "event" ? "2px dashed rgba(255,255,255,0.5)" : "0" };
    if (it.cancelled_for_holiday) {
      style.opacity = 0.35; style.textDecoration = "line-through";
    }
    if (it.kind === "holiday") {
      style.backgroundColor = "#e2e8f0"; style.color = "#475569"; style.fontWeight = 500;
    }
    return { style };
  };

  const onSelectEvent = (event) => {
    const it = event.resource;
    if (it.kind === "event" && canEdit) setEditingEvent(it);
    else if (it.kind === "class") toast.info(`${it.title} — open the class to edit schedule`);
  };

  const onEventDrop = async ({ event, start, end }) => {
    const it = event.resource;
    if (it.kind !== "event") {
      toast.error(t("calendar.classNotDraggable"));
      return;
    }
    if (!window.confirm(t("calendar.confirmReschedule"))) return;
    try {
      await http.post(`/calendar/events/${it.id}/reschedule`, {
        start: new Date(start).toISOString(), end: new Date(end).toISOString(),
      });
      toast.success(t("calendar.rescheduled"));
      fetchItems();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.conflicts ? t("classes.conflictsFound") : "Reschedule failed");
    }
  };

  const messages = i18n.language === "es" ? {
    today: "Hoy", previous: "Atrás", next: "Siguiente", month: "Mes", week: "Semana", day: "Día", agenda: "Agenda",
    date: "Fecha", time: "Hora", event: "Evento", noEventsInRange: "Sin eventos",
  } : undefined;

  return (
    <PageChrome title={t("calendar.title")} subtitle={t("calendar.subtitle")}>
      <style>{`
        @media print {
          aside, .topbar, .calendar-toolbar-controls { display: none !important; }
          .rbc-calendar { background: white !important; }
        }
        .rbc-event { padding: 2px 4px !important; font-size: 12px !important; }
      `}</style>

      <div className="calendar-toolbar-controls flex flex-wrap items-center gap-3 mb-4">
        <Select value={teacherFilter} onValueChange={setTeacherFilter}>
          <SelectTrigger className="w-[180px] bg-white" data-testid="cal-filter-teacher"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")} — {t("classes.teacher")}</SelectItem>
            {teachers.map(tc => <SelectItem key={tc.user_id} value={tc.user_id}>{tc.full_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={roomFilter} onValueChange={setRoomFilter}>
          <SelectTrigger className="w-[150px] bg-white"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")} — {t("classes.room")}</SelectItem>
            {rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[150px] bg-white"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            <SelectItem value="class">{t("calendar.kind_class")}</SelectItem>
            <SelectItem value="event">{t("calendar.kind_event")}</SelectItem>
            <SelectItem value="holiday">{t("calendar.kind_holiday")}</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="cal-print"><Printer className="h-4 w-4 mr-2"/>{t("calendar.print")}</Button>
          {canCreate && <Button onClick={() => setCreating(true)} className="bg-[#0A1F44]" data-testid="cal-add-event"><Plus className="h-4 w-4 mr-2"/>{t("calendar.addEvent")}</Button>}
        </div>
      </div>

      <div className="bg-white p-3 rounded-xl border border-slate-200" style={{ height: 720 }}>
        <DnDCalendar
          localizer={localizer}
          events={events}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          views={["month","week","day","agenda"]}
          startAccessor="start"
          endAccessor="end"
          eventPropGetter={eventStyle}
          onSelectEvent={onSelectEvent}
          onEventDrop={onEventDrop}
          onEventResize={onEventDrop}
          draggableAccessor={(e) => e.resource?.kind === "event"}
          resizable
          messages={messages}
          culture={i18n.language === "es" ? "es" : "en-US"}
        />
      </div>

      {(creating || editingEvent) && (
        <EventEditModal
          open onClose={() => { setCreating(false); setEditingEvent(null); }}
          initial={editingEvent}
          rooms={rooms} teachers={teachers}
          onSaved={() => { setCreating(false); setEditingEvent(null); fetchItems(); }}
          isAdmin={isAdmin}
        />
      )}
    </PageChrome>
  );
}
